import React, { useState, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  SimpleGrid,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Spinner,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
} from '@chakra-ui/react';
import { FiTrendingUp, FiRefreshCw, FiUsers, FiBarChart } from 'react-icons/fi';
import { usePlanRestriction } from '../../context/PlanRestrictionContext';
import { useAIAnalytics } from '../../services/aiAnalyticsService';

// Lazy load components for better performance
const RiskAssessmentTable = React.lazy(() => import('./RiskAssessmentTable'));
const AttendancePredictionsChart = React.lazy(() => import('./AttendancePredictionsChart'));
const PatternAnalysisCards = React.lazy(() => import('./PatternAnalysisCards'));
const RealTimeAlerts = React.lazy(() => import('./RealTimeAlerts'));
const TurnoverSummaryCards = React.lazy(() => import('./TurnoverSummaryCards'));
const TurnoverRiskChart = React.lazy(() => import('./TurnoverRiskChart'));
const TurnoverPredictionsTable = React.lazy(() => import('./TurnoverPredictionsTable'));

const AIAnalyticsDashboard = ({ selectedBranch = 'all' }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { canAccessFeature, showFeatureRestriction } = usePlanRestriction();
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // AI Analytics data
  const {
    // Summary data
    summaryData,
    summaryError,
    summaryIsLoading,
    
    // Risk assessment
    riskData,
    riskError,
    riskIsLoading,
    
    // Attendance predictions
    predictionsData,
    predictionsError,
    predictionsIsLoading,
    
    // Patterns data
    patternsData,
    patternsError,
    patternsIsLoading,
    
    // Turnover data
    turnoverData,
    turnoverError,
    turnoverIsLoading,
    
    // Turnover summary
    turnoverSummaryData,
    turnoverSummaryError,
    turnoverSummaryIsLoading,
    
    // Loading states
    isLoading,
    isRiskLoading,
    isPredictionsLoading,
    isPatternsLoading,
    isTurnoverSummaryLoading,
    isTurnoverPredictionsLoading,
    
    // Error states
    hasError,
    
    // Actions
    refetchAll,
  } = useAIAnalytics({
    enabled: true, // All Main Branch users now have access
    branch_id: selectedBranch
  });

  // All hooks must be called before any conditional returns
  const handleRefreshAlerts = useCallback(() => {
    refetchAll();
  }, [refetchAll]);

  const handleTabChange = useCallback((index) => {
    setActiveTab(index);
  }, []);

  // Calculate loading progress for current tab
  const getTabLoadingProgress = useCallback((tabIndex) => {
    if (tabIndex === 0) {
      // Attendance Analytics tab
      const totalSteps = 4;
      const completedSteps = [
        !summaryIsLoading, // Summary loaded
        !isRiskLoading, // Risk assessment loaded
        !isPredictionsLoading, // Predictions loaded
        !isPatternsLoading, // Patterns loaded
      ].filter(Boolean).length;
      return (completedSteps / totalSteps) * 100;
    } else {
      // Turnover Analytics tab
      const totalSteps = 2;
      const completedSteps = [
        !isTurnoverSummaryLoading, // Turnover summary loaded
        !isTurnoverPredictionsLoading, // Turnover predictions loaded
      ].filter(Boolean).length;
      return (completedSteps / totalSteps) * 100;
    }
  }, [summaryIsLoading, isRiskLoading, isPredictionsLoading, isPatternsLoading, isTurnoverSummaryLoading, isTurnoverPredictionsLoading]);

  // Feature access is now only restricted by user role (branch vs main branch)
  // All Main Branch users have access to AI Analytics regardless of plan

  // Show error if summary fails to load (critical for dashboard)
  if (summaryError) {
    return (
      <Box w="100%" bg="gray.50" minH="100vh" p={6}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>Failed to Load AI Analytics</AlertTitle>
            <AlertDescription>
              There was an error loading the AI-powered analytics. Please check your connection and try again.
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box p={6} bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Heading size="lg" color="gray.700">
                  AI Analytics Dashboard
                </Heading>
                <Text fontSize="sm" color="gray.500">
                  AI-powered insights and predictive analytics
                </Text>
              </VStack>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={handleRefreshAlerts}
                leftIcon={<FiRefreshCw />}
              >
                Refresh Data
              </Button>
            </HStack>

            {/* Progress Bar for Current Tab */}
            {getTabLoadingProgress(activeTab) < 100 && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">
                    Loading {activeTab === 0 ? 'attendance' : 'turnover'} analytics...
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {Math.round(getTabLoadingProgress(activeTab))}%
                  </Text>
                </HStack>
                <Progress
                  value={getTabLoadingProgress(activeTab)}
                  colorScheme="blue"
                  size="sm"
                  borderRadius="md"
                />
              </Box>
            )}
          </VStack>
        </Box>

        {/* Navigation Tabs */}
        <Box bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor} overflow="hidden">
          <Tabs index={activeTab} onChange={handleTabChange} variant="enclosed">
            <TabList bg="gray.50" borderBottom="1px solid" borderColor={borderColor}>
              <Tab 
                _selected={{ bg: cardBg, borderBottomColor: cardBg }}
                _hover={{ bg: 'gray.100' }}
                fontWeight="600"
              >
                <HStack spacing={2}>
                  <FiUsers />
                  <Text>Attendance Analytics</Text>
                  {summaryData && (
                    <Badge colorScheme="blue" size="sm">
                      {summaryData.total_employees || 0}
                    </Badge>
                  )}
                </HStack>
              </Tab>
              <Tab 
                _selected={{ bg: cardBg, borderBottomColor: cardBg }}
                _hover={{ bg: 'gray.100' }}
                fontWeight="600"
              >
                <HStack spacing={2}>
                  <FiBarChart />
                  <Text>Turnover Analytics</Text>
                  {turnoverSummaryData && (
                    <Badge colorScheme="purple" size="sm">
                      {turnoverSummaryData.total_employees || 0}
                    </Badge>
                  )}
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* Attendance Analytics Tab */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  {/* Summary Cards - Show immediately with loading state */}
                  <React.Suspense fallback={<Box p={6} bg={cardBg} borderRadius="lg"><Spinner /></Box>}>
                    <PatternAnalysisCards 
                      data={summaryData} 
                      isLoading={summaryIsLoading}
                      error={summaryError}
                    />
                  </React.Suspense>

                  {/* Risk Assessment - Show immediately with loading state */}
                  <React.Suspense fallback={<Box p={6} bg={cardBg} borderRadius="lg"><Spinner /></Box>}>
                    <RiskAssessmentTable 
                      data={riskData} 
                      isLoading={isRiskLoading}
                      error={riskError}
                    />
                  </React.Suspense>

                  {/* Predictions Chart - Show immediately with loading state */}
                  <React.Suspense fallback={<Box p={6} bg={cardBg} borderRadius="lg"><Spinner /></Box>}>
                    <AttendancePredictionsChart 
                      data={predictionsData} 
                      isLoading={isPredictionsLoading}
                      error={predictionsError}
                    />
                  </React.Suspense>

                  {/* Real-Time Alerts - Show immediately with loading state */}
                  <React.Suspense fallback={<Box p={6} bg={cardBg} borderRadius="lg"><Spinner /></Box>}>
                    <RealTimeAlerts 
                      data={patternsData} 
                      isLoading={isPatternsLoading}
                      error={patternsError}
                      onRefresh={handleRefreshAlerts} 
                    />
                  </React.Suspense>

                  {/* Error Handling for Attendance Tab */}
                  {(riskError || predictionsError || patternsError) && (
                    <Alert status="warning" borderRadius="lg">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>Partial Data Loaded</AlertTitle>
                        <AlertDescription>
                          Some attendance analytics components failed to load. The available data is displayed above.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              </TabPanel>

              {/* Turnover Analytics Tab */}
              <TabPanel p={6} w="100%">
                <VStack spacing={6} align="stretch" w="100%">
                  {/* Turnover Summary Cards */}
                  <React.Suspense fallback={<Box p={6} bg={cardBg} borderRadius="lg"><Spinner /></Box>}>
                    <TurnoverSummaryCards
                      summaryData={turnoverSummaryData}
                      isLoading={isTurnoverSummaryLoading}
                      error={turnoverSummaryError}
                    />
                  </React.Suspense>

                  {/* Turnover Risk Chart */}
                  <React.Suspense fallback={<Box p={6} bg={cardBg} borderRadius="lg"><Spinner /></Box>}>
                    <TurnoverRiskChart
                      summaryData={turnoverSummaryData}
                      isLoading={isTurnoverSummaryLoading}
                      error={turnoverSummaryError}
                    />
                  </React.Suspense>

                  {/* Turnover Predictions Table - Full Width */}
                  <React.Suspense fallback={<Box p={6} bg={cardBg} borderRadius="lg"><Spinner /></Box>}>
                    <TurnoverPredictionsTable
                      turnoverData={turnoverData}
                      isLoading={isTurnoverPredictionsLoading}
                      error={turnoverError}
                    />
                  </React.Suspense>

                  {/* Error Handling for Turnover Tab */}
                  {(turnoverError || turnoverSummaryError) && (
                    <Alert status="warning" borderRadius="lg">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>Partial Data Loaded</AlertTitle>
                        <AlertDescription>
                          Some turnover analytics components failed to load. The available data is displayed above.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
    </Box>
  );
};

export default AIAnalyticsDashboard; 